import { useCallback, useRef } from 'react';

type SoundType = 'startRecording' | 'stopRecording' | 'success' | 'error' | 'complete' | 'login';

interface UseSoundsReturn {
  playSound: (type: SoundType) => void;
  setSoundEnabled: (enabled: boolean) => void;
  isSoundEnabled: () => boolean;
}

const SOUND_ENABLED_KEY = 'litrlly_sounds_enabled';

const useSounds = (): UseSoundsReturn => {
  const audioContextRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback((): AudioContext => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const isSoundEnabled = useCallback((): boolean => {
    const stored = localStorage.getItem(SOUND_ENABLED_KEY);
    return stored === null ? true : stored === 'true';
  }, []);

  const setSoundEnabled = useCallback((enabled: boolean): void => {
    localStorage.setItem(SOUND_ENABLED_KEY, String(enabled));
  }, []);

  const playTone = useCallback((
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    volume: number = 0.15,
    fadeOut: boolean = true
  ): void => {
    const ctx = getAudioContext();

    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

    gainNode.gain.setValueAtTime(volume, ctx.currentTime);

    if (fadeOut) {
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    }

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  }, [getAudioContext]);

  const playChime = useCallback((frequencies: number[], duration: number, volume: number = 0.1): void => {
    const ctx = getAudioContext();

    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    frequencies.forEach((freq, index) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, ctx.currentTime);

      const startTime = ctx.currentTime + (index * 0.08);
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    });
  }, [getAudioContext]);

  const playSound = useCallback((type: SoundType): void => {
    if (!isSoundEnabled()) return;

    try {
      switch (type) {
        case 'startRecording':
          // Rising soft tone - indicates recording started
          const ctx1 = getAudioContext();
          if (ctx1.state === 'suspended') ctx1.resume();

          const osc1 = ctx1.createOscillator();
          const gain1 = ctx1.createGain();
          osc1.connect(gain1);
          gain1.connect(ctx1.destination);

          osc1.type = 'sine';
          osc1.frequency.setValueAtTime(440, ctx1.currentTime);
          osc1.frequency.exponentialRampToValueAtTime(880, ctx1.currentTime + 0.15);

          gain1.gain.setValueAtTime(0.12, ctx1.currentTime);
          gain1.gain.exponentialRampToValueAtTime(0.001, ctx1.currentTime + 0.2);

          osc1.start(ctx1.currentTime);
          osc1.stop(ctx1.currentTime + 0.2);
          break;

        case 'stopRecording':
          // Falling soft tone - indicates recording stopped
          const ctx2 = getAudioContext();
          if (ctx2.state === 'suspended') ctx2.resume();

          const osc2 = ctx2.createOscillator();
          const gain2 = ctx2.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx2.destination);

          osc2.type = 'sine';
          osc2.frequency.setValueAtTime(660, ctx2.currentTime);
          osc2.frequency.exponentialRampToValueAtTime(440, ctx2.currentTime + 0.12);

          gain2.gain.setValueAtTime(0.12, ctx2.currentTime);
          gain2.gain.exponentialRampToValueAtTime(0.001, ctx2.currentTime + 0.15);

          osc2.start(ctx2.currentTime);
          osc2.stop(ctx2.currentTime + 0.15);
          break;

        case 'success':
          // Pleasant ascending chime - success/good result
          playChime([523.25, 659.25, 783.99], 0.3, 0.1); // C5, E5, G5
          break;

        case 'complete':
          // Two-tone completion sound
          playChime([440, 554.37, 659.25], 0.35, 0.08); // A4, C#5, E5
          break;

        case 'error':
          // Low soft tone - error notification
          playTone(220, 0.25, 'sine', 0.1);
          setTimeout(() => playTone(196, 0.3, 'sine', 0.08), 150);
          break;

        case 'login':
          // Welcoming ascending arpeggio - login success
          playChime([392, 493.88, 587.33, 783.99], 0.4, 0.12); // G4, B4, D5, G5
          break;
      }
    } catch (err) {
      // Silently fail if audio context is not available
      console.warn('Sound playback failed:', err);
    }
  }, [getAudioContext, isSoundEnabled, playChime, playTone]);

  return {
    playSound,
    setSoundEnabled,
    isSoundEnabled,
  };
};

export default useSounds;
